/**
 * KanbanViewOpCentral.tsx - OpCentral-compliant Kanban board with full orchestrator integration
 * 
 * CHANGE HISTORY:
 * - 251107_2250 [current] danielbeer: feat: Create OpCentral-compliant KanbanView with Interfacr integration
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DndContext, DragOverlay, closestCorners, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { KanbanCore } from './KanbanCore';
import { KanbanConfig } from './KanbanConfig';
import KanbanColumn from './KanbanColumn';
import KanbanCard from './KanbanCard';
import { KanbanContextMenu, ContextMenuItem } from './KanbanContextMenu';
import './KanbanView.css';

// OpCentral Orchestrator Interfaces (type-only to avoid circular deps)
interface InterfacrInstance {
  execute: (params: any) => Promise<any>;
}

interface ProfylrInstance {
  activeProfile?: any;
}

interface KanbanViewOpCentralProps {
  config: KanbanConfig;
  data?: any[];
  onDataChange?: (data: any[]) => void;
  
  // OpCentral Orchestrators
  interfacr?: InterfacrInstance;
  profylr?: ProfylrInstance;
  
  // Workspace context
  workspaceId?: string;
  datasetId?: string;
  
  // Optional overrides
  columns?: any[];
  onItemClick?: (itemId: string) => void;
}

export const KanbanViewOpCentral: React.FC<KanbanViewOpCentralProps> = ({
  config,
  data,
  onDataChange,
  interfacr,
  profylr,
  workspaceId,
  datasetId,
  columns: customColumns,
  onItemClick,
}) => {
  const coreRef = useRef<KanbanCore | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<any>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; card: any } | null>(null);
  
  // ==========================================================================
  // INTERFACR HANDLERS (UI OPERATIONS)
  // ==========================================================================
  
  /**
   * Handle undo through Interfacr → Backtrackr
   */
  const handleUndo = useCallback(async () => {
    if (interfacr) {
      try {
        await interfacr.execute({
          type: 'backtrackr',
          operation: 'undo',
        });
      } catch (error) {
        console.error('Interfacr undo failed:', error);
      }
    } else if (config.onUndo) {
      config.onUndo();
    }
  }, [interfacr, config]);
  
  /**
   * Handle redo through Interfacr → Backtrackr
   */
  const handleRedo = useCallback(async () => {
    if (interfacr) {
      try {
        await interfacr.execute({
          type: 'backtrackr',
          operation: 'redo',
        });
      } catch (error) {
        console.error('Interfacr redo failed:', error);
      }
    } else if (config.onRedo) {
      config.onRedo();
    }
  }, [interfacr, config]);
  
  /**
   * Show notification through Interfacr → Notifyr
   */
  const showNotification = useCallback(async (
    message: string,
    variant: 'success' | 'error' | 'info' | 'warning' = 'info'
  ) => {
    if (interfacr) {
      try {
        await interfacr.execute({
          type: 'notification',
          operation: 'show',
          params: { message, variant },
        });
      } catch (error) {
        console.error('Interfacr notification failed:', error);
      }
    }
  }, [interfacr]);
  
  /**
   * Show confirm dialog through Interfacr → Promptr/Dialoggr
   */
  const showConfirmDialog = useCallback(async (
    title: string,
    message: string
  ): Promise<boolean> => {
    if (interfacr) {
      try {
        return await interfacr.execute({
          type: 'dialog',
          operation: 'confirm',
          params: { title, message, variant: 'warning' },
        });
      } catch (error) {
        console.error('Interfacr dialog failed:', error);
      }
    }
    return window.confirm(message);
  }, [interfacr]);
  
  /**
   * Show card edit form through Interfacr → Formr
   */
  const showCardEditForm = useCallback(async (cardData: any): Promise<any> => {
    if (interfacr) {
      try {
        return await interfacr.execute({
          type: 'form',
          operation: 'show',
          params: {
            title: 'Edit Card',
            fields: [
              { name: 'title', label: 'Title', type: 'text', value: cardData.title, required: true },
              { name: 'description', label: 'Description', type: 'textarea', value: cardData.description },
              { name: 'assignee', label: 'Assignee', type: 'text', value: cardData.assignee },
              { name: 'priority', label: 'Priority', type: 'select', value: cardData.priority, options: [
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
              ]},
              { name: 'dueDate', label: 'Due Date', type: 'date', value: cardData.dueDate },
            ],
          },
        });
      } catch (error) {
        console.error('Interfacr form failed:', error);
      }
    }
    return null;
  }, [interfacr]);
  
  /**
   * Register keyboard shortcuts through Interfacr → Commandr
   */
  const registerKeyboardShortcuts = useCallback(async () => {
    if (interfacr && config.enableKeyboardNav) {
      try {
        await interfacr.execute({
          type: 'commandr',
          operation: 'register',
          params: {
            context: 'kanban',
            shortcuts: [
              { key: 'n', action: 'kanban:new-card', description: 'New card in selected column' },
              { key: 'e', action: 'kanban:edit-card', description: 'Edit selected card' },
              { key: 'Delete', action: 'kanban:delete-card', description: 'Delete selected card' },
              { key: 'Escape', action: 'kanban:clear-selection', description: 'Clear selection' },
              { key: 'ArrowLeft', action: 'kanban:previous-column', description: 'Previous column' },
              { key: 'ArrowRight', action: 'kanban:next-column', description: 'Next column' },
              { key: 'ArrowUp', action: 'kanban:previous-card', description: 'Previous card' },
              { key: 'ArrowDown', action: 'kanban:next-card', description: 'Next card' },
            ],
          },
        });
      } catch (error) {
        console.error('Interfacr commandr registration failed:', error);
      }
    }
  }, [interfacr, config.enableKeyboardNav]);
  
  // ==========================================================================
  // CORE INITIALIZATION
  // ==========================================================================
  
  /**
   * Initialize core on mount
   */
  useEffect(() => {
    coreRef.current = new KanbanCore({
      ...config,
      workspaceId,
      datasetId,
      columns: customColumns || config.columns,
    });
    
    // Subscribe to state changes
    const unsubscribe = coreRef.current.subscribe((newState) => {
      setState(newState);
      if (onDataChange) {
        onDataChange(newState.data);
      }
    });
    
    // Load initial data
    if (data) {
      coreRef.current.setData(data);
    } else {
      coreRef.current.loadData();
    }
    
    // Initial state
    setState(coreRef.current.getState());
    
    // Register keyboard shortcuts
    registerKeyboardShortcuts();
    
    return unsubscribe;
  }, []);
  
  /**
   * Update data when prop changes
   */
  useEffect(() => {
    if (coreRef.current && data) {
      coreRef.current.setData(data);
    }
  }, [data]);
  
  // ==========================================================================
  // KEYBOARD NAVIGATION
  // ==========================================================================
  
  useEffect(() => {
    if (!config.enableKeyboardNav) return;
    
    const handleKeyDown = async (e: KeyboardEvent) => {
      const core = coreRef.current;
      if (!core) return;
      
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      
      // Undo (Cmd+Z)
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        await handleUndo();
      }
      // Redo (Cmd+Shift+Z or Cmd+Y)
      else if ((cmdOrCtrl && e.shiftKey && e.key === 'z') || (cmdOrCtrl && e.key === 'y')) {
        e.preventDefault();
        await handleRedo();
      }
      // New card (n)
      else if (e.key === 'n' && !cmdOrCtrl) {
        e.preventDefault();
        // TODO: Add card to first column or selected column
        await showNotification('New card shortcut pressed', 'info');
      }
      // Delete card (Delete)
      else if (e.key === 'Delete' && !cmdOrCtrl) {
        e.preventDefault();
        const selection = core.getSelection();
        if (selection.count > 0) {
          const confirmed = await showConfirmDialog(
            'Delete Cards',
            `Delete ${selection.count} card(s)?`
          );
          if (confirmed) {
            for (const cardId of selection.cardIds) {
              await core.deleteCard(cardId);
            }
            await showNotification(`Deleted ${selection.count} card(s)`, 'success');
          }
        }
      }
      // Clear selection (Escape)
      else if (e.key === 'Escape') {
        e.preventDefault();
        core.clearSelection();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [config.enableKeyboardNav, handleUndo, handleRedo, showNotification, showConfirmDialog]);
  
  // ==========================================================================
  // DRAG-AND-DROP HANDLERS
  // ==========================================================================
  
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    const core = coreRef.current;
    if (core) {
      const columnId = active.data.current?.columnId;
      core.startDrag(active.id as string, columnId);
    }
  }, []);
  
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    
    const core = coreRef.current;
    if (!core || !over) {
      core?.endDrag();
      return;
    }
    
    const cardId = active.id as string;
    const overColumnId = over.data.current?.type === 'card' 
      ? over.data.current?.columnId 
      : over.id as string;
    
    // Move card through core (which routes through Actr)
    const success = await core.moveCard(cardId, overColumnId);
    
    if (success) {
      await showNotification('Card moved', 'success');
    } else {
      await showNotification('Failed to move card', 'error');
    }
    
    core.endDrag();
  }, [showNotification]);
  
  // ==========================================================================
  // CARD HANDLERS
  // ==========================================================================
  
  const handleCardClick = useCallback(async (cardId: string) => {
    const core = coreRef.current;
    if (!core) return;
    
    // Select card
    core.selectCard(cardId);
    
    // Call optional click handler
    if (onItemClick) {
      onItemClick(cardId);
    }
  }, [onItemClick]);
  
  const handleCardEdit = useCallback(async (cardId: string) => {
    const core = coreRef.current;
    if (!core) return;
    
    const card = core.getCard(cardId);
    if (!card) return;
    
    const result = await showCardEditForm(card);
    if (result) {
      await core.updateCard(cardId, result);
      await showNotification('Card updated', 'success');
    }
  }, [showCardEditForm, showNotification]);
  
  const handleCardDelete = useCallback(async (cardId: string) => {
    const core = coreRef.current;
    if (!core) return;
    
    const card = core.getCard(cardId);
    if (!card) return;
    
    const confirmed = await showConfirmDialog(
      'Delete Card',
      `Delete "${card.title || 'this card'}"?`
    );
    
    if (confirmed) {
      await core.deleteCard(cardId);
      await showNotification('Card deleted', 'success');
    }
  }, [showConfirmDialog, showNotification]);
  
  // ==========================================================================
  // CONTEXT MENU
  // ==========================================================================
  
  const handleCardContextMenu = useCallback((e: React.MouseEvent, card: any) => {
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      card,
    });
  }, []);
  
  const getContextMenuItems = useCallback((): ContextMenuItem[] => {
    if (!contextMenu?.card) return [];
    
    const card = contextMenu.card;
    
    return [
      {
        id: 'edit',
        label: 'Edit Card',
        icon: '✏️',
        action: () => handleCardEdit(card.id),
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        icon: '📋',
        action: async () => {
          const core = coreRef.current;
          if (core) {
            const newCard = { ...card, id: `card-${Date.now()}`, title: `${card.title} (Copy)` };
            await core.addCard(newCard);
            await showNotification('Card duplicated', 'success');
          }
        },
      },
      {
        id: 'separator-1',
        label: '',
        separator: true,
        action: () => {},
      },
      {
        id: 'move-todo',
        label: 'Move to To Do',
        icon: '📋',
        action: async () => {
          const core = coreRef.current;
          if (core) {
            await core.moveCard(card.id, 'todo');
            await showNotification('Card moved', 'success');
          }
        },
      },
      {
        id: 'move-in-progress',
        label: 'Move to In Progress',
        icon: '🔄',
        action: async () => {
          const core = coreRef.current;
          if (core) {
            await core.moveCard(card.id, 'in-progress');
            await showNotification('Card moved', 'success');
          }
        },
      },
      {
        id: 'move-review',
        label: 'Move to Review',
        icon: '👀',
        action: async () => {
          const core = coreRef.current;
          if (core) {
            await core.moveCard(card.id, 'review');
            await showNotification('Card moved', 'success');
          }
        },
      },
      {
        id: 'move-done',
        label: 'Move to Done',
        icon: '✅',
        action: async () => {
          const core = coreRef.current;
          if (core) {
            await core.moveCard(card.id, 'done');
            await showNotification('Card moved', 'success');
          }
        },
      },
      {
        id: 'separator-2',
        label: '',
        separator: true,
        action: () => {},
      },
      {
        id: 'delete',
        label: 'Delete Card',
        icon: '🗑️',
        danger: true,
        action: () => handleCardDelete(card.id),
      },
    ];
  }, [contextMenu, handleCardEdit, handleCardDelete, showNotification]);
  
  // ==========================================================================
  // PROFYLR INTEGRATION (PROFILE-AWARE UI)
  // ==========================================================================
  
  const tone = profylr?.activeProfile?.perspective?.tone || 'professional';
  const style = profylr?.activeProfile?.perspective?.style || 'default';
  
  // Helper to get field value - MUST be before any early returns
  const getField = useCallback((item: any, fieldName: string) => {
    const mapping = config.fieldMappings?.[fieldName as keyof typeof config.fieldMappings];
    return mapping ? item[mapping] : item[fieldName];
  }, [config.fieldMappings]);
  
  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  if (!state) {
    return <div className="kanban-loading">Loading...</div>;
  }
  
  const activeCard = activeId ? state.data.find((card: any) => 
    (config.fieldMappings?.idField ? card[config.fieldMappings.idField] : card.id) === activeId
  ) : null;
  
  return (
    <div
      ref={containerRef}
      className={`kanban-view tone-${tone} style-${style}`}
      data-profile-tone={tone}
      data-profile-style={style}
      tabIndex={0}
      style={{ outline: 'none' }}
    >
      <DndContext
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-columns">
          {state.columns.map((column: any) => {
            const core = coreRef.current;
            const columnCards = core ? core.getCardsInColumn(column.id) : [];
            
            return (
              <KanbanColumn
                key={column.id}
                id={column.id}
                title={column.title}
                color={column.color}
                items={columnCards}
                getField={getField}
                onAddCard={() => {
                  // TODO: Implement add card
                }}
                onCardClick={handleCardClick}
                onCardContextMenu={handleCardContextMenu}
                columnStyles={config.columnStyles}
                cardStyles={config.cardStyles}
              />
            );
          })}
        </div>
        
        <DragOverlay>
          {activeCard ? (
            <KanbanCard
              item={activeCard}
              getField={getField}
              onClick={() => {}}
              onContextMenu={() => {}}
              cardStyles={config.cardStyles}
              columnId={getField(activeCard, 'columnField')}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      
      {/* Context Menu */}
      {contextMenu && (
        <KanbanContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems()}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default KanbanViewOpCentral;
